import React, {useState, useEffect, useMemo} from 'react'

import { useLocation, Link } from 'react-router-dom'
import { Button, LoadingFull, DataTable, Chip, SelectAsync } from '@arnsbogroup/react-voicearchive'
import Icon from 'components/Icon'
import { useIntl, FormattedDate } from 'react-intl'
import SearchPage from 'containers/SearchPage'
import classNames from 'classnames'
import useApi from 'hooks/useApi'
import { Span, Timestamp, Contact } from 'components/datatable'
import debounce from 'debounce-promise'

const CustomerTags = ({ isMarketingAdvertising, isMediaProduction, hasRecentCommercial }) => (
  <div className="flex flex-wrap gap-1">
    { !!parseInt(isMarketingAdvertising) && <Chip size="xs" state="info" title="Customer Industry" >Marketing &amp; Advertising</Chip> }
    { !!parseInt(isMediaProduction) && <Chip size="xs" state="info" title="Customer Industry" >Media Production</Chip> }
    { !!parseInt(hasRecentCommercial) && <Chip size="xs" state="warning" title="Order product (last 3 orders)" >Commercial</Chip> }
  </div>
)

const Activities = ({ activities = [] }) => {
  const assigned = activities.find(({ action }) => action == 'idle-assigned')
  const actions = activities.filter(({ action }) => action == 'idle-activity')

  return (
    <div className="flex items-center space-x-4">
      { !activities.length ? (
        <Chip size="xs" state="error">No activities yet</Chip>
      ) : !!actions.length ? (
        <Chip size="xs" state="warning">In progress</Chip>
      ) : (
        <Chip size="xs" state="warning">Assigned</Chip>
      ) }

      <Contact {...assigned} />
    </div>
  )
}

const ActivityContact = ({ activities }) => {
  const assigned = activities.find(({ action }) => action == 'idle-assigned')

  if (assigned) {
    return <Contact {...assigned} />
  }

  return null
}

const CustomersIdlePage = ({ history, ...props }) => {
  const [customerList, loading, errors, refetch] = useApi(`/rest/v1/customer/idle`)
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([])
  const [assiging, setAssigning] = useState(false)
  const [coordinators, setCoordinators] = useState([])
  const location = useLocation()
  const [sorting, setSorting] = useState({
    key: '',
    direction: 'DESC',
  })

  const onAssign = () => {
    setAssigning(true)

    fetch(`/rest/v1/customer/reactivate`, {
      method: 'POST',
      body: JSON.stringify({
        coordinatorIds: coordinators.map(({ value }) => value),
        customerIds: selectedCustomerIds,
      })
    })
      .then(response => response.json())
      .then(() => setSelectedCustomerIds([]))
      .then(() => setAssigning(false))
      .then(refetch)
  }

  const customers = useMemo(() => {
    return customerList?.sort((a, b) => {
      if (sorting.direction === 'DESC') {
        return parseInt(b[sorting.key]) - parseInt(a[sorting.key])
      }

      return parseInt(a[sorting.key]) - parseInt(b[sorting.key])
    }) || []
  }, [customerList, sorting])

  return (
    <div className="grow overflow-y-scroll flex flex-col">
      <div className="shrink-0 flex items-center justify-between p-2">
        <h2 className="font-medium text-sm text-grayscale-lighter">
          Customers, created after 1/1/2017, with more than 3 orders that has not placed an order in the last 3 months. Filtered to "Marketing &amp; Advertising" and "Media Production" customer Industry, or customers with a Commercial product in their last 3 orders.
        </h2>

        <div className="flex items-center justify-between space-x-2">
          <div className="w-96">
            <SelectAsync
              size="sm"
              isMulti
              value={coordinators}
              onChange={(value) => setCoordinators(value)}
              cacheOptions
              loadOptions={
                debounce((query) => fetch(`/rest/v2/coordinator?pageSize=25&search[name]=${query}`)
                  .then(response => response.json())
                  .then((coordinators) => coordinators.map(({ uid, name }) => ({ value: uid, label: name })))
                  , 250)
              }
              openMenuOnFocus
              placeholder="Distribute and assign to user(s)"
            />
          </div>

          <Button size="sm" disabled={!coordinators.length} onClick={onAssign}>Assign</Button>
        </div>
      </div>



      <div className="grow overflow-scroll">
        { (loading || assiging) ? (
          <LoadingFull />
        ) : (
          <DataTable
            components={{}}
            head={{
              activities: { sortable: false, label: 'Reactivation Activities' },
              uid: { sortable: false, className: 'w-16', label: 'ID' },
              company_id: { sortable: false, className: 'w-16', label: 'CRM' },
              name: { sortable: false, label: 'Name' },
              tags: { sortable: false, className: 'w-32', label: 'Tags' },
              totalOrders: { sortable: true, label: '# of orders' },
              totalRevenue: { sortable: true, label: 'Revenue' },
              latestOrderTimestamp: { sortable: true, label: 'Latest Order' },
              initialOrderTimestamp: { sortable: true, label: 'Initial Order' },
            }}
            body={{
              activities: Activities,
              uid: Span,
              company_id: ({ company_id }) => (company_id > 0 ?
                <a
                  onClick={(e) => e.stopPropagation()}
                  href={`https://my.arnsbogroup.com/crm/company/details/${company_id}/`}
                  target="_blank"
                  className="text-primary hover:text-primary-darker"
                >
                  { company_id } <Icon icon="external-link" className="text-xs" />
                </a> : null
              ),
              name: ({ name, uid }) => <Link className="hover:text-primary" to={`/customers/${uid}`}>{ name }</Link>,
              tags: CustomerTags,
              totalOrders: Span,
              totalRevenue: Span,
              latestOrderTimestamp: Timestamp,
              initialOrderTimestamp: Timestamp,
            }}
            entries={customers}
            indicators={[]}
            messages={{}}
            state={{}}
            rowSelectionKey="uid"
            rowsSelected={selectedCustomerIds}
            onRowSelected={(key) => setSelectedCustomerIds((prev) => {
              if (prev.includes(key)) {
                return prev.filter((id) => id !== key)
              }
              return [...prev, key]
            })}
            sorting={sorting}
            setSorting={setSorting}
          />
        ) }
      </div>
    </div>
  )
}

export default CustomersIdlePage
